import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "bp_session_id";
const UTM_KEY = "bp_utm";
const CONSENT_KEY = "bp_analytics_consent";

type Props = Record<string, unknown>;

let queue: Array<Record<string, unknown>> = [];
let flushTimer: number | null = null;

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "no-session";
  }
}

function getStoredUtm(): Record<string, string | null> {
  try {
    const raw = sessionStorage.getItem(UTM_KEY);
    if (raw) return JSON.parse(raw);
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string | null> = {
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      utm_term: params.get("utm_term"),
      utm_content: params.get("utm_content"),
    };
    sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
    return utm;
  } catch {
    return {};
  }
}

export function hasAnalyticsConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === "granted";
  } catch {
    return false;
  }
}

export function setAnalyticsConsent(granted: boolean) {
  try {
    localStorage.setItem(CONSENT_KEY, granted ? "granted" : "denied");
  } catch {
    /* noop */
  }
}

export function consentDecisionMade(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) !== null;
  } catch {
    return true;
  }
}

async function flush() {
  if (queue.length === 0) return;
  const events = queue.splice(0, queue.length);
  try {
    await supabase.functions.invoke("ingest-analytics", { body: { events } });
  } catch {
    /* drop on error */
  }
}

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flush();
  }, 1500);
}

export function track(event: string, props: Props = {}) {
  if (!hasAnalyticsConsent()) return;
  if (queue.length > 50) return; // drop on flood
  const utm = getStoredUtm();
  queue.push({
    session_id: getSessionId(),
    event_name: event,
    properties: props,
    page_path: typeof window !== "undefined" ? window.location.pathname : null,
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    ...utm,
  });
  scheduleFlush();
}

export function pageview(path?: string) {
  track("page_view", { path: path ?? window.location.pathname });
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => void flush());
  document.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement | null)?.closest?.("[data-cta]");
    if (el) track("cta_clicked", { cta: el.getAttribute("data-cta") });
  });
}
